// BM-H4141 - what the CLIENT reads in the LPA questionnaire's live checks.
//
// The LPA engine writes for the fee earner: "Replacement at index 0 is also named as attorney at index 0",
// statute references, "Cross-check identifier ref: jonathan ronnald|reeves|CT28QJ", "Record consideration in
// the file note", and a rule code as the heading. Liezel's UAT run on 9 Oct 2026 showed every one of those on
// the client's screen. The client needs one plain sentence that names the person and says what to do. Staff
// still see the engine's own wording on View submission (FeeEarnerFormReview), which this does not touch.

import { LPA_SECTIONS } from "../LPAFormSections";

const PATH_RE = /^([a-z_]+)(?:\[(\d+)\])?/;

function personName(p) {
  if (!p) return "";
  return [p.first_names, p.last_name].map((s) => String(s || "").trim()).filter(Boolean).join(" ");
}

function personAt(instrument, path) {
  const m = PATH_RE.exec(path || "");
  if (!m) return "";
  const [, key, idx] = m;
  const holder = (instrument || {})[key];
  if (idx !== undefined) return personName(Array.isArray(holder) ? holder[Number(idx)] : null);
  return personName(holder);
}

const SECTION_BY_PATH = [
  ["attorneys", "s3_attorneys"],
  ["decision_mode", "s3_attorneys"],
  ["replacement_mode", "s4_replacements"],
  ["replacements", "s4_replacements"],
  ["when_attorneys_can_act", "s5_when_act"],
  ["life_sustaining_treatment", "s5_when_act"],
  ["preferences_and_instructions.preferences", "s6_preferences"],
  ["preferences_and_instructions", "s7_instructions"],
  ["notified_persons", "s8_notified"],
  ["cert_provider", "s9_certificate"],
  ["donor", "s1_donor"],
];

export function clientSectionLabel(path) {
  const p = path || "";
  const hit = SECTION_BY_PATH.find(([prefix]) => p === prefix || p.startsWith(`${prefix}.`) || p.startsWith(`${prefix}[`));
  const id = hit ? hit[1] : "s10_review";
  return LPA_SECTIONS.find((s) => s.id === id)?.label || "";
}

const or = (name, fallback) => name || fallback;

// One sentence per rule the client can actually act on. Anything else falls back to a neutral line: the
// capacity and undue pressure rules in particular are for the fee earner to raise in person, never to be
// spelt out on the client's screen.
const SENTENCES = {
  "V-A01_attorney_under_18": (n) => `${or(n, "One of your attorneys")} is under 18. Every attorney must be 18 or over when the LPA is signed. Please choose someone else.`,
  "V-A03_attorney_bankrupt_lpa_pf": (n) => `${or(n, "One of your attorneys")} is shown as bankrupt or under a debt relief order, so cannot be an attorney for property and financial affairs. Please choose someone else.`,
  "V-X02_attorney_bankrupt_lpa_pf_cross_cutter": () => null, // the same point as V-A03; say it once
  "V-A04_duplicate_attorney": (n) => `${or(n, "One person")} is entered twice as an attorney. Please remove the second entry.`,
  // BM-H4150: same name and date of birth, different postcode. Probably one person entered twice.
  "V-A04b_possible_duplicate_attorney": (n) => `${or(n, "One of your attorneys")} appears twice with the same date of birth but a different address. If this is the same person, please remove the second entry.`,
  "V-R02b_possible_replacement_also_attorney": (n) => `${or(n, "Your replacement attorney")} has the same name and date of birth as one of your attorneys. If this is the same person, please remove them from one of the two lists.`,
  "V-A05_attorney_address_matches_donor": (n) => `${or(n, "One of your attorneys")} lives at the same address as you. That is allowed. Your fee earner may ask you about it.`,
  "V-R01_replacement_under_18": (n) => `${or(n, "Your replacement attorney")} is under 18. A replacement attorney must be 18 or over. Please choose someone else.`,
  "V-R02_replacement_also_attorney": (n) => `${or(n, "One person")} is named both as an attorney and as a replacement attorney. A person can be one or the other, not both. Please remove one of them.`,
  "V-DM01_sole_expected_multiple_selected": () => "You have named one attorney, so please choose \"One attorney acts alone\" for how they make decisions.",
  "V-DM02_multiple_expected_sole_selected": () => "You have named more than one attorney. Please choose how they should make decisions together.",
  "V-DM03_mixed_specification_missing": () => "Please describe which decisions your attorneys must make together.",
  "V-C02_cert_provider_basis_missing": () => "Please say how your certificate provider knows you: for at least two years, or through their professional skills.",
  "V-C01_cert_provider_excluded_reg_8_2": () => "Your certificate provider cannot be a family member, a business partner, an employee, or an attorney. Please choose someone else.",
  "V-C03_cert_provider_identity_matches_attorney_or_replacement": (n) => `Your certificate provider${n ? `, ${n},` : ""} is also one of your attorneys or replacement attorneys. The certificate provider must be someone else.`,
  "V-N01_notified_person_also_attorney_or_replacement": (n) => `${or(n, "One of the people to tell")} is also one of your attorneys or replacement attorneys. People to tell must be someone else.`,
  "V-LST01_lst_missing_on_lpa_hw": () => "Please choose Option A or Option B for life-sustaining treatment.",
  "V-R03_jointly_zero_replacements": () => "Your attorneys must act together on every decision, and you have no replacement. If one of them cannot act, the LPA would stop working. You may want to add a replacement attorney.",
  "V-X03_jointly_zero_replacements_collapse_risk": () => null, // the same point as V-R03; say it once
  "V-R04_mixed_zero_replacements": () => "Some decisions must be made by your attorneys together, and you have no replacement. You may want to add a replacement attorney.",
  "V-N02_no_notified_persons_recommended_layer": () => "You have not named anyone to tell. That is allowed. Your fee earner may talk to you about it.",
};

const FALLBACK_STOP = "Your fee earner needs to look at this with you before the LPA can be prepared. They will contact you, or you can ask for a call.";
const FALLBACK_NOTE = "Your fee earner will go through this with you.";

function plain(item, instrument, fallback) {
  const fn = SENTENCES[item.code];
  if (fn) return fn(personAt(instrument, item.path));
  return fallback;
}

// Engine result (flat LPA shape) -> the { flags } shape EngineResultView reads, in the client's words.
export function toClientEngineView(result, instrument) {
  if (!result) return null;
  const stops = [];
  for (const hs of result.hard_stops || []) {
    const sentence = plain(hs, instrument, FALLBACK_STOP);
    if (sentence) stops.push({ code: hs.code, reason: sentence, section: clientSectionLabel(hs.path) });
  }
  const notes = [];
  for (const a of result.advisories || []) {
    const sentence = plain(a, instrument, FALLBACK_NOTE);
    if (sentence) notes.push({ code: a.code, title: "Please check", message: sentence, section: clientSectionLabel(a.path) });
  }
  // The same fallback line under one section once is enough.
  const once = (list, text) => list.filter((x, i) => list.findIndex((y) => text(y) === text(x) && y.section === x.section) === i);
  return {
    flags: {
      hard_stops: once(stops, (x) => x.reason),
      warnings: once(notes, (x) => x.message),
    },
  };
}
